import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Progress as PxProgress } from '@roi-pros/px-react';
import toast from 'react-hot-toast';
import { buildBillingReturnUrl, resolveReturnTo } from '../../../utils/billingReturn';

interface IncludedCredits {
  monthly: number;
  used: number;
  remaining: number;
  resetDate: string | null;
  status: 'active' | 'pending';
}

interface TrialCredits {
  remaining: number;
  endsAt: string | null;
}

interface CreditBalance {
  available: number;
  included: IncludedCredits;
  purchased: {
    remaining: number;
  };
  trial?: TrialCredits;
}

interface CreditSource {
  plan: {
    code: string;
    name: string;
    baseCredits: number;
  };
  outcomes: Array<{
    code: string;
    displayName: string;
    includedCredits: number;
  }>;
}

interface CreditPack {
  packCode: string;
  credits: number;
  /** null when Central has no valid price for the pack (C0). */
  amountCents: number | null;
  displayName: string;
  /** false when Central has no valid price for the pack (C0). */
  priceAvailable?: boolean;
}

interface CreditsTabProps {
  workspaceId: string;
  authToken: string;
}

interface PurchaseError {
  message: string;
  /** The token is for another workspace: offer the workspace switcher. */
  switchWorkspace?: boolean;
}

const CHECKOUT_UNAVAILABLE = 'Checkout is temporarily unavailable. You were not charged. Please try again.';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * C0: a pack is purchasable only with Central's own valid price — a positive
 * integer amount in cents for a positive integer number of credits (the same
 * rule the purchase API enforces). Anything else reads "Unavailable", never $0.
 */
function isPackPriced(pack: CreditPack): boolean {
  return (
    pack.priceAvailable !== false &&
    typeof pack.amountCents === 'number' && Number.isInteger(pack.amountCents) && pack.amountCents > 0 &&
    typeof pack.credits === 'number' && Number.isInteger(pack.credits) && pack.credits > 0
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Credits settings tab
 *
 * Features:
 * - Available balance
 * - Included/purchased/trial credits
 * - Reset date
 * - Credit sources breakdown
 * - Available packs for purchase
 *
 * C0: this is the suite's canonical purchase screen
 * (/workspace/<ws>/settings/credits?return_to=<app page>): choose a pack →
 * price and total → Continue → Central creates Checkout → Stripe →
 * /billing/return → return_to?billing=success|cancel. Cancel/Back returns to
 * the validated return_to, else this workspace's billing page. Packs and
 * prices come only from Central; members (canPurchase false) get no purchase UI.
 */
export function CreditsTab({ workspaceId, authToken }: CreditsTabProps) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [sources, setSources] = useState<CreditSource | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [purchasingPack, setPurchasingPack] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  // C0: the read failed (never shown as a 0 balance); retryable.
  const [loadError, setLoadError] = useState(false);
  // C0: the purchase API's own owner/admin rule, reported by the credits read.
  // Absent (unknown) fails closed: no purchase UI.
  const [canPurchase, setCanPurchase] = useState(false);
  const [selectedPackCode, setSelectedPackCode] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<PurchaseError | null>(null);

  // Contract §1.2: `return_to` (or the settings page's `returnUrl`) is where
  // a purchase started from another app should end up. `purchase=` is set by
  // Stripe's default return to this page; `billing=` by /billing/return.
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || searchParams.get('returnUrl');
  const purchaseResult = searchParams.get('purchase') || searchParams.get('billing');

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  // Back from Stripe: say what actually happened, refresh, and strip the
  // one-shot parameter so a reload does not repeat the message.
  const [refreshAfterPurchase, setRefreshAfterPurchase] = useState(false);

  useEffect(() => {
    if (purchaseResult !== 'success' && purchaseResult !== 'cancel') return;
    if (purchaseResult === 'success') {
      setNotice('Payment received. Your credits are added as soon as the payment is confirmed — usually within a minute.');
      setRefreshAfterPurchase(true);
    } else {
      setNotice('Purchase canceled. You were not charged.');
    }
    const params = new URLSearchParams(searchParams);
    params.delete('purchase');
    params.delete('billing');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseResult]);

  // The webhook adds purchased credits; read the balance again shortly after.
  useEffect(() => {
    if (!refreshAfterPurchase) return;
    const t = setTimeout(() => loadData(), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAfterPurchase]);

  async function loadData() {
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/credits`, authToken, {
        
      });

      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance || null);
        setSources(data.sources || null);
        setPacks(Array.isArray(data.packs) ? data.packs : []);
        setCanPurchase(data.canPurchase === true);
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    } catch {
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }

  // C0: Cancel / Back — the validated return_to (Central path, or an absolute
  // URL Central's redirect allowlist approves), else this workspace's billing
  // page. Never a raw return_to.
  async function handleCancel() {
    const target = returnTo ? await resolveReturnTo(returnTo) : null;
    if (target) {
      window.location.assign(target);
    } else {
      navigate(`/workspace/${workspaceId}/settings/billing`);
    }
  }

  async function handlePurchasePack(packCode: string) {
    const pack = packs.find((p) => p.packCode === packCode);
    // C0: never start checkout for a pack without a valid Central price.
    if (!pack || !isPackPriced(pack) || !canPurchase) return;
    setPurchasingPack(packCode);
    setPurchaseError(null);
    try {
      // Where Stripe returns the browser. Started from another app (return_to
      // present) → Central /billing/return, which validates return_to and
      // appends billing=success|cancel. Otherwise → this page with
      // purchase=success|cancel so the result is shown here.
      const origin = window.location.origin;
      const here = `${origin}${window.location.pathname}`;
      const successUrl = returnTo
        ? buildBillingReturnUrl({ origin, status: 'success', kind: 'credits', workspaceId, returnTo, withStripeSessionId: true })
        : `${here}?purchase=success`;
      const cancelUrl = returnTo
        ? buildBillingReturnUrl({ origin, status: 'cancel', kind: 'credits', workspaceId, returnTo })
        : `${here}?purchase=cancel`;

      const response = await authFetch(`/api/workspaces/${workspaceId}/credits/purchase`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packCode, successUrl, cancelUrl }),
      });

      const data = await response.json().catch(() => null);
      if (response.ok) {
        // BIL-01: the API answers { mode: 'checkout', checkoutUrl } (Stripe) or
        // { mode: 'direct', ... } (development/test only, no payment taken).
        // This used to read `data.url`, which the API never sends — so every
        // real purchase fell through to a false "Credits purchased" toast.
        if (data?.mode === 'checkout' && typeof data.checkoutUrl === 'string') {
          window.location.assign(data.checkoutUrl);
          return;
        }
        if (data?.mode === 'direct') {
          toast.success('Credits added (test environment — no payment was taken)');
          loadData();
          return;
        }
        setPurchaseError({ message: 'Could not start checkout. Please try again.' });
      } else if (data?.code === 'STRIPE_NOT_CONFIGURED') {
        setPurchaseError({ message: 'Credit purchases are temporarily unavailable. Please try again later.' });
      } else if (data?.code === 'PACK_PRICE_UNAVAILABLE') {
        setPurchaseError({ message: 'This credit pack is not available for online purchase. Contact us to buy credits.' });
      } else if (data?.code === 'WORKSPACE_ACCESS_DENIED') {
        setPurchaseError({
          message: 'You are signed in to a different workspace. Switch to this workspace to buy credits for it.',
          switchWorkspace: true,
        });
      } else if (response.status === 403) {
        setPurchaseError({ message: 'Only workspace owners and admins can buy credits. Ask your workspace owner to add credits.' });
      } else if (response.status >= 500 || !data) {
        setPurchaseError({ message: CHECKOUT_UNAVAILABLE });
      } else {
        setPurchaseError({ message: data?.error || 'Could not start checkout. Please try again.' });
      }
    } catch {
      setPurchaseError({ message: CHECKOUT_UNAVAILABLE });
    } finally {
      setPurchasingPack(null);
    }
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  // B7 adoption (review finding): the usage fraction passed to the Platform Progress must
  // always be a finite 0..1 value, so the determinate progressbar never advertises an
  // invalid aria-valuenow. This derivation stays application-owned:
  //   monthly > 0        -> used / monthly, capped at 1 (same fraction the prior markup used)
  //   monthly <= 0       -> 0 (e.g. zero-included-credit / free plans; avoids 0/0 = NaN)
  //   non-finite results -> 0
  const rawUsageRatio =
    balance && balance.included.monthly > 0
      ? balance.included.used / balance.included.monthly
      : 0;
  const usageProgress = Number.isFinite(rawUsageRatio) ? Math.min(1, rawUsageRatio) : 0;

  // C0: only a priced pack can be the selection (price + total + Continue).
  const selectedPack = packs.find((p) => p.packCode === selectedPackCode && isPackPriced(p)) ?? null;

  return (
    <div className="space-y-6">
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" role="status">
          <p className="text-sm text-emerald-800">{notice}</p>
        </div>
      )}

      {loadError && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4" role="alert">
          <p className="text-sm text-amber-900">Credits are temporarily unavailable. Please try again.</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
          >
            Try again
          </button>
        </div>
      )}

      {/* Available Balance — unknown is "—", never 0 (C0) */}
      <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-lg shadow-sm p-6 text-white">
        <h2 className="text-sm font-medium text-emerald-100">Available Credits</h2>
        <p className="text-4xl font-bold mt-2" data-testid="credits-available">
          {!loadError && typeof balance?.available === 'number' && Number.isFinite(balance.available) ? balance.available : '—'}
        </p>
        {balance?.included.resetDate && balance.included.status === 'active' && (
          <p className="text-sm text-emerald-200 mt-2">
            Resets {formatDate(balance.included.resetDate)}
          </p>
        )}
      </div>

      {/* Credit Breakdown */}
      {balance && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Credit Breakdown</h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Included Credits */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Monthly Included</p>
                <p className="text-sm text-gray-500">
                  {balance.included.remaining} remaining of {balance.included.monthly}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{balance.included.remaining}</p>
                {balance.included.status === 'pending' && (
                  <span className="text-xs text-amber-600 font-medium">Pending</span>
                )}
              </div>
            </div>

            {/* Usage Progress */}
            <div className="px-4">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                <span>Used this period</span>
                <span>{balance.included.used} / {balance.included.monthly}</span>
              </div>
              {/* B7 Platform adoption: the usage bar now renders via the Platform Experience
                  Progress component (@roi-pros/px-react). The application owns the derived
                  `usageProgress` value (see above); the Platform owns HOW the bar renders
                  (track/fill tokens, determinate progressbar a11y). Presentation only —
                  no call sites changed. */}
              <PxProgress api={{ value: usageProgress }} />
            </div>

            {/* Purchased Credits */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-900">Purchased Credits</p>
                <p className="text-sm text-gray-500">Never expire</p>
              </div>
              <p className="text-2xl font-bold text-green-600">{balance.purchased.remaining}</p>
            </div>

            {/* Trial Credits */}
            {balance.trial && balance.trial.remaining > 0 && (
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">Trial Credits</p>
                  {balance.trial.endsAt && (
                    <p className="text-sm text-gray-500">
                      Expires {formatDate(balance.trial.endsAt)}
                    </p>
                  )}
                </div>
                <p className="text-2xl font-bold text-amber-600">{balance.trial.remaining}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credit Sources */}
      {sources && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Credit Sources</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {/* Plan Credits */}
            <div className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{sources.plan.name} Plan</p>
                <p className="text-sm text-gray-500">Base plan credits</p>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                +{sources.plan.baseCredits}
              </p>
            </div>

            {/* Outcome Credits */}
            {sources.outcomes.filter((o) => o.includedCredits > 0).map((outcome) => (
              <div key={outcome.code} className="px-6 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{outcome.displayName}</p>
                  <p className="text-sm text-gray-500">Outcome bonus credits</p>
                </div>
                <p className="text-lg font-semibold text-emerald-600">
                  +{outcome.includedCredits}
                </p>
              </div>
            ))}

            {sources.outcomes.filter((o) => o.includedCredits > 0).length === 0 && (
              <div className="px-6 py-4 text-center text-gray-500 text-sm">
                Add outcomes to earn bonus credits each month
              </div>
            )}
          </div>
        </div>
      )}

      {/* Purchase Credits — C0: the suite's canonical purchase screen.
          Choose a pack → price and total → Continue (Central creates Checkout)
          or Cancel (validated return_to, else the billing page). */}
      {!loadError && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5" data-testid="credit-purchase">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Purchase Credits</h2>
            <p className="text-sm text-gray-500 mt-1">
              Purchased credits never expire and are used after monthly credits
            </p>
          </div>
          {!canPurchase ? (
            <div className="p-6">
              <p className="text-sm text-gray-700" data-testid="credits-ask-owner">
                Only workspace owners and admins can buy credits. Ask your workspace owner to add credits.
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <>
              {packs.length === 0 ? (
                <p className="px-6 pt-6 text-sm text-gray-700">
                  Credit packs are not available for online purchase right now. Contact us to buy credits.
                </p>
              ) : (
                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-4" role="radiogroup" aria-label="Credit packs">
                  {packs.map((pack) => {
                    const priced = isPackPriced(pack);
                    const isSelected = priced && selectedPackCode === pack.packCode;
                    const isPopular = pack.packCode === 'pack-1200';

                    return (
                      <label
                        key={pack.packCode}
                        data-testid={`credit-pack-${pack.packCode}`}
                        className={`relative block rounded-lg border-2 p-4 ${
                          priced ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'
                        } ${
                          isSelected
                            ? 'border-emerald-600 bg-emerald-50'
                            : isPopular
                              ? 'border-emerald-300 bg-white'
                              : 'border-gray-200 bg-white'
                        }`}
                      >
                        {isPopular && (
                          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-emerald-600 text-white text-xs font-medium rounded">
                            Most Popular
                          </span>
                        )}
                        <input
                          type="radio"
                          name="credit-pack"
                          value={pack.packCode}
                          checked={isSelected}
                          disabled={!priced || purchasingPack !== null}
                          onChange={() => {
                            setSelectedPackCode(pack.packCode);
                            setPurchaseError(null);
                          }}
                          aria-label={pack.displayName}
                          className="absolute top-3 left-3 h-4 w-4"
                        />
                        <div className="text-center">
                          <p className="text-2xl font-bold text-gray-900">{pack.credits}</p>
                          <p className="text-sm text-gray-500">credits</p>
                          {priced ? (
                            <>
                              <p className="text-xl font-semibold text-gray-900 mt-3">
                                {formatCents(pack.amountCents as number)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {((pack.amountCents as number) / pack.credits).toFixed(1)}¢ per credit
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-xl font-semibold text-gray-500 mt-3">Unavailable</p>
                              <p className="text-xs text-gray-500">Contact us</p>
                            </>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="px-6 pb-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 pt-4 mt-6 sm:mt-0">
                  <dl className="text-sm text-gray-700 space-y-1">
                    <div className="flex gap-2">
                      <dt className="text-gray-500">Selected:</dt>
                      <dd data-testid="credit-purchase-selected">
                        {selectedPack ? `${selectedPack.credits.toLocaleString('en-US')} credits` : 'None'}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="text-gray-500">Total:</dt>
                      <dd className="font-semibold text-gray-900" data-testid="credit-purchase-total">
                        {selectedPack ? formatCents(selectedPack.amountCents as number) : '—'}
                      </dd>
                    </div>
                  </dl>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={purchasingPack !== null}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => selectedPack && handlePurchasePack(selectedPack.packCode)}
                      disabled={!selectedPack || purchasingPack !== null}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {purchasingPack ? 'Redirecting to checkout…' : 'Continue to checkout'}
                    </button>
                  </div>
                </div>
                {purchaseError && (
                  <div
                    className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"
                    role="alert"
                    data-testid="credit-purchase-error"
                  >
                    {purchaseError.message}
                    {purchaseError.switchWorkspace && (
                      <a
                        href={`/auth/workspace?continue=${encodeURIComponent(window.location.pathname + (window.location.search || ''))}`}
                        className="ml-2 font-medium underline"
                      >
                        Switch workspace
                      </a>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
